import { useCallback, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useLocale } from '../i18n/LocaleContext'

type ConfirmationRequest = { message: string; title?: string; danger?: boolean; notice?: boolean }

export function useAppConfirm() {
  const { t } = useLocale()
  const [request, setRequest] = useState<ConfirmationRequest | null>(null)
  const resolver = useRef<((accepted: boolean) => void) | null>(null)

  const confirm = useCallback((message: string, title?: string, danger = false) =>
    new Promise<boolean>((resolve) => {
      resolver.current?.(false)
      resolver.current = resolve
      setRequest({ message, title, danger })
    }), [])

  const notice = useCallback((message: string, title?: string) => {
    resolver.current?.(false)
    resolver.current = null
    setRequest({ message, title, notice: true })
  }, [])

  const settle = useCallback((accepted: boolean) => {
    resolver.current?.(accepted)
    resolver.current = null
    setRequest(null)
  }, [])

  useEffect(() => () => resolver.current?.(false), [])

  const confirmationDialog = request ? <CenteredConfirmationDialog
    title={request.title ?? t('dialog.confirmTitle')}
    message={request.message}
    cancelLabel={t('dialog.cancel')}
    confirmLabel={t('dialog.confirm')}
    danger={request.danger ?? false}
    notice={request.notice ?? false}
    onCancel={() => settle(false)}
    onConfirm={() => settle(true)}
  /> : null

  return { confirm, notice, confirmationDialog }
}

function CenteredConfirmationDialog({ title, message, cancelLabel, confirmLabel, danger, notice, onCancel, onConfirm }: {
  title: string
  message: string
  cancelLabel: string
  confirmLabel: string
  danger: boolean
  notice: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const titleId = useId()
  const surfaceRef = useRef<HTMLElement>(null)
  const cancelRef = useRef(onCancel)
  cancelRef.current = onCancel

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    const previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null
    document.body.style.overflow = 'hidden'
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') cancelRef.current()
      if (event.key !== 'Tab' || !surfaceRef.current) return
      const controls = [...surfaceRef.current.querySelectorAll<HTMLElement>(
        'button:not(:disabled), [href], input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
      )]
      if (controls.length === 0) return
      const first = controls[0]
      const last = controls[controls.length - 1]
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus() }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus() }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.body.style.overflow = previousOverflow
      document.removeEventListener('keydown', handleKeyDown)
      previousFocus?.focus()
    }
  }, [])

  return createPortal(<div className="modal-backdrop app-confirm-backdrop" onMouseDown={onCancel}>
    <section ref={surfaceRef} className="administration-modal app-confirm-modal" role="alertdialog"
      aria-modal="true" aria-labelledby={titleId} onMouseDown={event => event.stopPropagation()}>
      <header><h2 id={titleId}>{title}</h2><button className="modal-close" type="button"
        aria-label={cancelLabel} onClick={onCancel}>×</button></header>
      <div className="administration-modal-body"><div className="app-confirm-dialog">
        <p>{message}</p>
        <div className="form-actions">
          {!notice && <button className="secondary-button" type="button" autoFocus onClick={onCancel}>{cancelLabel}</button>}
          <button className={danger ? 'danger-button' : 'primary-button'} type="button"
            autoFocus={notice} onClick={notice ? onCancel : onConfirm}>{confirmLabel}</button>
        </div>
      </div></div>
    </section>
  </div>, document.body)
}
